/**
 * jrs-byob-guard.js — Build Your Own Bundle: re-check kit rewards against the cart as it is now (window.JRS.Byob.Guard).
 *
 * 2026-10-08 (Hamza): a shopper can build a qualifying kit, then remove items on the cart page and keep the
 * rewards (e.g. 3 items from 2 categories -> remove 2 -> 1 polo with a £0 logo). The builder grants rewards
 * at add time only; nothing re-checked them after. This file re-checks every kit in the cart:
 *
 *   - Counts per _byob_kit_id: garment units (lines with no _ParentVariant and no _byob_gift) and categories
 *     (distinct _byob_category on those lines). Same tier rules as jrs-byob-core.js Byob.computeTier.
 *   - Unearned gift (tier 2 / tier 3 cap or beanie, lines carrying _byob_gift): removed from the cart, with
 *     every line of that gift group (garment + its logo line), so the cart validator sees no orphan.
 *   - Unearned free logo (a £0 position line on a kit garment while the kit is below tier 1): checkout blocked
 *     with a shopper message and one fix: remove the bundle.
 *   - Free item outside any kit or bundle (a £0 line titled "Free …" or a byob-free-gift-* product with no
 *     _byob_kit_id and no _bundle_instance_id, e.g. added by direct URL): checkout blocked, one fix: remove it.
 *
 * Hooks, no new checkout path: wraps JRS.CartValidator.validateCart (heal step -> gift prune) and
 * validateBeforeCheckout (gate -> free-logo check). Both theme checkout routes call these two first:
 * the cart page Checkout buttons (jrs-v2-cart-page.js _onCheckoutClick) and the builder's
 * "Go to checkout" (jrs-byob-builder-b.js goToCheckout). Also renders the notice on /cart on load and
 * after every cart:updated.
 *
 * Reads properties only; never writes or renames a line-item property. Lines are matched by
 * _byob_kit_id and the cart line key, never by _lineItemKey.
 *
 * Client-side only: a direct /cart/add.js call, a /cart/<variant>:<qty> permalink or a typed /checkout
 * never runs this file. Those holes need a server-side check (see the thread record).
 *
 * Load order: after jrs-v2-event-bus.js and jrs-v2-cart-validator.js (layout/theme.liquid, deferred).
 */
(function () {
  'use strict';

  var JRS = (window.JRS = window.JRS || {});
  var Byob = (JRS.Byob = JRS.Byob || {});
  if (Byob.Guard) return;

  // Same ladder as jrs-byob-core.js Byob.TIERS (core loads on the builder page only, so it is repeated here).
  var TIERS = [
    { tier: 1, items: 3, categories: 2 },
    { tier: 2, items: 6, categories: 3 },
    { tier: 3, items: 10, categories: 3 }
  ];

  function computeTier(items, categories) {
    var reached = 0;
    for (var i = 0; i < TIERS.length; i++) {
      if (items >= TIERS[i].items && categories >= TIERS[i].categories) reached = TIERS[i].tier;
    }
    return reached;
  }

  function props(it) { return (it && it.properties) || {}; }
  function blank(v) { return v === undefined || v === null || v === ''; }
  function unitPrice(it) { return Number(it.original_price != null ? it.original_price : it.price) || 0; }

  /**
   * One entry per kit in the cart.
   * { kitId, units, categories, tier, giftKeys: { t2: [keys], t3: [keys] }, freeLogoKeys: [keys], keys: [all keys] }
   */
  function evaluate(cart) {
    var kits = {};
    var order = [];
    (cart && cart.items || []).forEach(function (it) {
      var p = props(it);
      var kitId = p._byob_kit_id;
      if (blank(kitId)) return;
      var k = kits[kitId];
      if (!k) {
        k = kits[kitId] = { kitId: kitId, units: 0, cats: {}, legacyCats: Number(p._byob_categories) || 0, hasCatProp: false,
          giftKeys: { t2: [], t3: [] }, freeLogoKeys: [], keys: [] };
        order.push(kitId);
      }
      k.keys.push(it.key);
      var gift = p._byob_gift;
      if (gift === 'tier2') { k.giftKeys.t2.push(it.key); return; }
      if (gift === 'tier3') { k.giftKeys.t3.push(it.key); return; }
      if (!blank(p._ParentVariant)) {
        if (unitPrice(it) === 0) k.freeLogoKeys.push(it.key);
        return;
      }
      k.units += Number(it.quantity) || 0;
      if (!blank(p._byob_category)) { k.cats[p._byob_category] = true; k.hasCatProp = true; }
    });
    return order.map(function (id) {
      var k = kits[id];
      // Kits added before _byob_category existed: fall back to the count stamped at add time.
      var categories = k.hasCatProp ? Object.keys(k.cats).length : k.legacyCats;
      k.categories = categories;
      k.tier = computeTier(k.units, categories);
      k.unearnedGiftKeys = []
        .concat(k.tier < 2 ? k.giftKeys.t2 : [])
        .concat(k.tier < 3 ? k.giftKeys.t3 : []);
      k.freeLogoUnearned = k.tier < 1 && k.freeLogoKeys.length > 0;
      return k;
    });
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function zeroOut(keys) {
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  function track(name, k) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, kit_id: k.kitId, items: k.units, categories: k.categories, tier: k.tier });
    } catch (e) { /* analytics never blocks the guard */ }
  }

  /** Remove gifts the kit no longer earns. Resolves the cart after any removal (or the cart passed in). */
  function pruneGifts(cart) {
    var kits = evaluate(cart);
    var keys = [];
    kits.forEach(function (k) {
      if (k.unearnedGiftKeys.length) { keys = keys.concat(k.unearnedGiftKeys); track('byob_gift_removed', k); }
    });
    if (!keys.length) return Promise.resolve({ cart: cart, removed: 0 });
    return zeroOut(keys).then(function (next) {
      try { sessionStorage.setItem('jrs_byob_gift_removed', '1'); } catch (e) {}
      if (JRS.EventBus) JRS.EventBus.emit('cart:updated', { cart: next });
      return { cart: next, removed: keys.length };
    });
  }

  function message(k) {
    return 'Your bundle now has ' + k.units + (k.units === 1 ? ' item' : ' items') + ' from ' + k.categories +
      (k.categories === 1 ? ' category' : ' categories') + '. The free logo needs 3 items from at least 2 categories. ' +
      'Remove the bundle to continue to checkout.';
  }

  /** First kit whose free logo is no longer earned, or null. */
  function blockingKit(cart) {
    var kits = evaluate(cart);
    for (var i = 0; i < kits.length; i++) if (kits[i].freeLogoUnearned) return kits[i];
    return null;
  }

  /**
   * £0 reward products in the cart with no kit and no pre-made bundle behind them. Pre-made bundles
   * (sections/bundle-new.liquid) also use the "Free <position>" products, always with _bundle_instance_id.
   */
  function strayFreeKeys(cart) {
    var keys = [];
    (cart && cart.items || []).forEach(function (it) {
      var p = props(it);
      if (unitPrice(it) !== 0 || !blank(p._byob_kit_id) || !blank(p._bundle_instance_id)) return;
      var title = String(it.product_title || it.title || '');
      var handle = String(it.handle || '');
      if (/^free\s/i.test(title) || /^byob-free-gift/.test(handle)) keys.push(it.key);
    });
    return keys;
  }

  var STRAY_MSG = 'Your cart has a free item that is only free inside a Build Your Own Bundle. Remove it to continue to checkout.';

  // ---------------------------------------------------------------------------
  // Cart page notice
  // ---------------------------------------------------------------------------

  function onCartPage() { return /^\/cart\/?$/.test(window.location.pathname); }

  function renderNotice(cart) {
    if (!onCartPage()) return;
    var existing = document.querySelector('[data-byob-guard-notice]');
    var k = blockingKit(cart);
    var giftNote = false;
    try { giftNote = sessionStorage.getItem('jrs_byob_gift_removed') === '1'; sessionStorage.removeItem('jrs_byob_gift_removed'); } catch (e) {}
    var stray = k ? [] : strayFreeKeys(cart);
    if (!k && !stray.length && !giftNote) { if (existing) existing.remove(); return; }
    var anchor = document.querySelector('[data-jrs-checkout-btn]');
    if (!anchor || !anchor.parentNode) return;
    if (!existing) {
      existing = document.createElement('div');
      existing.setAttribute('data-byob-guard-notice', '');
      existing.setAttribute('role', 'alert');
      existing.className = 'jrs-byob-guard';
      existing.style.cssText = 'margin:12px 0;padding:12px 14px;border:1px solid #FC5A2A;background:#fff4ef;' +
        'color:#1e1e1f;border-radius:8px;font-size:14px;line-height:1.45;';
      anchor.parentNode.insertBefore(existing, anchor);
    }
    if (!k && stray.length) {
      existing.innerHTML = '<p style="margin:0 0 10px">' + STRAY_MSG + '</p>' +
        '<button type="button" data-byob-guard-remove style="width:100%;padding:10px 12px;border-radius:6px;border:1px solid #1e1e1f;background:#fff;color:#1e1e1f;font-weight:600;cursor:pointer">Remove free item</button>';
      var sb = existing.querySelector('[data-byob-guard-remove]');
      sb.addEventListener('click', function () {
        sb.disabled = true;
        zeroOut(stray).then(function () { window.location.reload(); }, function () { window.location.reload(); });
      });
      return;
    }
    if (!k) {
      existing.textContent = 'Your bundle dropped below 6 items from 3 categories, so we took the free cap or beanie out of your cart.';
      return;
    }
    // 2026-10-08 (Hamza): one fix only, "Remove bundle"; no "Add items back" link.
    existing.innerHTML = '<p style="margin:0 0 10px">' + message(k) + '</p>' +
      '<button type="button" data-byob-guard-remove style="width:100%;padding:10px 12px;border-radius:6px;border:1px solid #1e1e1f;background:#fff;color:#1e1e1f;font-weight:600;cursor:pointer">Remove bundle</button>';
    var btn = existing.querySelector('[data-byob-guard-remove]');
    btn.addEventListener('click', function () {
      btn.disabled = true;
      track('byob_bundle_removed_after_downgrade', k);
      zeroOut(k.keys).then(function () { window.location.reload(); }, function () { window.location.reload(); });
    });
    track('byob_reward_blocked_shown', k);
  }

  function refresh(cart) {
    var p = cart && cart.items ? Promise.resolve(cart) : getCart();
    return p.then(pruneGifts).then(function (res) { renderNotice(res.cart); return res.cart; })
      .catch(function (e) { if (window.console) console.error('[byob-guard]', e); });
  }

  // ---------------------------------------------------------------------------
  // Validator wraps: the gate both theme checkout routes call before /checkout
  // ---------------------------------------------------------------------------

  function wrapValidator() {
    var v = JRS.CartValidator;
    if (!v || v.__byobGuarded) return !!v;
    var origValidate = v.validateCart;
    var origGate = v.validateBeforeCheckout;
    if (typeof origValidate === 'function') {
      v.validateCart = function () {
        var args = arguments;
        return Promise.resolve(origValidate.apply(v, args)).then(function (res) {
          return getCart().then(pruneGifts).then(function () { return res; }, function () { return res; });
        });
      };
    }
    if (typeof origGate === 'function') {
      v.validateBeforeCheckout = function () {
        return Promise.resolve(origGate.apply(v, arguments)).then(function (res) {
          if (res && res.canCheckout === false) return res;
          // Fail closed like the validator: an unreadable cart is not a cleared cart.
          return getCart().then(function (cart) {
            var k = blockingKit(cart);
            var stray = k ? [] : strayFreeKeys(cart);
            if (!k && !stray.length) return res;
            if (k) track('byob_reward_checkout_blocked', k);
            else track('byob_stray_free_blocked', { kitId: '', units: 0, categories: 0, tier: 0 });
            // The cart page shows the notice with its fix buttons; the block message then only points at it.
            var shown = onCartPage() && document.querySelector('[data-byob-guard-notice]');
            var text = shown ? 'Use the button above to continue to checkout.' : (k ? message(k) : STRAY_MSG);
            return {
              canCheckout: false,
              issues: [{ type: 'byob_reward', byob: true, message: text,
                details: k ? 'BYOB kit ' + k.kitId + ' below tier 1 with ' + k.freeLogoKeys.length + ' free logo line(s)'
                  : stray.length + ' free reward line(s) outside any kit or bundle', fixed: false }]
            };
          });
        });
      };
    }
    v.__byobGuarded = true;
    return true;
  }

  Byob.Guard = { evaluate: evaluate, blockingKit: blockingKit, strayFreeKeys: strayFreeKeys, pruneGifts: pruneGifts, refresh: refresh, computeTier: computeTier };

  function init() {
    wrapValidator();
    if (JRS.EventBus && typeof JRS.EventBus.on === 'function') {
      JRS.EventBus.on('cart:updated', function (d) { if (onCartPage()) refresh(d && d.cart); });
    }
    if (onCartPage()) refresh();
  }

  // The validator is deferred before this file, so it exists now; init the rest once the DOM is parsed.
  wrapValidator();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
